import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { CatHeading } from '@blocks-react/components/Heading';
import { PAGE_BREAKPOINTS } from '@app/constants/commonConstants';
import { useBreakpoint } from '@app/hooks';
import { CatTextPassage } from '@blocks-react/components/TextPassage';
import { CatDropdown } from '@blocks-react/components/Dropdown';
import { CatList } from '@blocks-react/components/List';
import { CatListItem } from '@blocks-react/components/ListItem';
import { CatTextareaField } from '@blocks-react/components/TextareaField';
import { CatInputField } from '@blocks-react/components/InputField';
import { CatButton } from '@blocks-react/components/Button';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  contactUsReasons,
  askAnExpertReasons,
  japaneseLocaleCode,
  ContactUsTabs
} from '../../constants';
import styles from './ContactUsForm.module.scss';
import cx from 'classnames';
import useContactUs from '../../hooks/useContactUs';
import Conditional from '@app/components/common/Conditional';
import { getCookie } from '@app/utils';

const ContactUsForm = ({ onError, onSuccess, tabAction = 'contact' }) => {
  const MD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const [t] = useTranslation();
  const { createInquiry } = useContactUs();

  const locale = useSelector(state => state.common.locale);
  const profileAddress = useSelector(state => state.common.profileAddress);
  const isPageInJapanese = locale === japaneseLocaleCode;

  const countries = useSelector(s => s?.dealer?.countryInfo || []);
  const areTranslationsLoaded = useSelector(
    s => s.common.areTranslationsLoaded
  );
  const PCC_CustomerSupportErrorEventLoggingPCCContactUsWebform = useSelector(
    s => s.featureFlag?.PCC_CustomerSupportErrorEventLoggingPCCContactUsWebform
  );
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showDropdowns, setShowDropdowns] = useState(true);

  const isLoadingContactUsPage =
    !useSelector(s => s.dealer?.getDealerInfoSuccess) || !showDropdowns;

  const {
    control,
    getValues,
    register,
    setValue,
    clearErrors,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      category: '',
      comments: '',
      serialNumber: '',
      firstName: '',
      lastName: '',
      email: '',
      country: '',
      city: '',
      state: '',
      zipCode: ''
    }
  });

  const contactUsReasonsWithTranslations = useMemo(() => {
    if (!areTranslationsLoaded) {
      return [];
    }
    const isContactUsTab = tabAction === ContactUsTabs.CONTACT;
    const categoryReasons = isContactUsTab
      ? contactUsReasons
      : askAnExpertReasons;
    return categoryReasons.map(reason => ({
      value: isContactUsTab ? reason.value : t(reason.label),
      label: t(reason.label),
      key: `${reason.label}${uuid()}`
    }));
  }, [areTranslationsLoaded, tabAction, t]);

  const statesListOfSelectedCountry = useMemo(() => {
    if (
      !selectedCountry ||
      !countries.some(country => country?.code === selectedCountry)
    ) {
      return [];
    }
    return countries
      .find(country => country?.code === selectedCountry)
      .states.map(state => ({
        value: state.code,
        label: state.displayName,
        key: `${state.code}${uuid()}`
      }));
  }, [countries, selectedCountry]);

  const countriesList = useMemo(() => {
    return countries.map(country => ({
      value: country.code,
      label: country.displayName,
      key: `${country.code}${uuid()}`
    }));
  }, [countries]);

  useEffect(() => {
    const referenceId = sessionStorage.getItem('referenceId');
    const PageTitle = sessionStorage.getItem('pageTitle');
    const translationKeyText = t('CONTACT_WEBFORM_CRITICAL_ERROR');
    let dataToPopulate, autoSelectCategoryFromError;
    if (referenceId && PageTitle) {
      dataToPopulate = translationKeyText
        .replace('{0}', `[${PageTitle}]`)
        .replace('{1}', `[${referenceId}]`);

      autoSelectCategoryFromError = t('SITE_ERROR_MESSAGING');
    }
    const fieldsToPopulate =
      PCC_CustomerSupportErrorEventLoggingPCCContactUsWebform
        ? {
            firstName: profileAddress?.firstName,
            lastName: profileAddress?.lastName,
            email: profileAddress?.email1,
            country: profileAddress?.country,
            city: profileAddress?.city,
            state: profileAddress?.state,
            zipCode: profileAddress?.zipCode,
            comments: PCC_CustomerSupportErrorEventLoggingPCCContactUsWebform
              ? dataToPopulate
              : '',
            category: autoSelectCategoryFromError
          }
        : {};

    Object.entries(fieldsToPopulate).forEach(([key, value]) => {
      if (value) {
        setValue(key, value);
        if (key === 'country') {
          setValue('country', '');
          setSelectedCountry(value);
        }
        if (key === 'category') {
          setSelectedCategory(value);
        }
      }
    });
    if (countriesList.length > 0) {
      setValue('country', profileAddress?.country);
    }
  }, [profileAddress, setValue, countriesList]);

  const commonProperties = (key, { required = true, errorMessage = '' }) => {
    return {
      fieldId: `dropdown-${key}`,
      'data-testid': key,
      name: key,
      ariadescribedby: `dropdown-field-aria-${key}`,
      notation: required
        ? t('REQUIRED_FIELD_MESSAGE')
        : (t('OPTIONAL') || '').toLowerCase(),
      isLoading: isLoadingContactUsPage || !areTranslationsLoaded,
      onBlChange: e => {
        clearErrors(key);
        setValue(key, e.target.value);
      },
      control: control,
      status: errors && errors[key] ? 'error' : undefined,
      errorNote: errors && errors[key]?.message,
      required: true,
      value: getValues(key),
      ...register(key, {
        required: {
          value: required,
          message: errorMessage
        }
      })
    };
  };

  const setInputValue = (key, value) => {
    setValue(key, value);
    clearErrors(key);
  };

  const commonInputProperties = (
    key,
    { required = true, errorMessage = '' }
  ) => {
    return {
      ...commonProperties(key, { required, errorMessage }),
      onBlChange: e => {
        setInputValue(key, e.target.value);
      }
    };
  };

  const handleSelectCountry = e => {
    setSelectedCountry(e?.detail.value);
    setInputValue('country', e?.detail.value);
    setSelectedState('');
    setInputValue('state', '');
  };

  const handleSelectCategory = e => {
    setSelectedCategory(e?.detail.value);
    setInputValue('category', e?.detail.value);
  };

  const handleSelectState = e => {
    setSelectedState(e?.detail.value);
    setInputValue('state', e?.detail.value);
  };

  const submitForm = async data => {
    onError(false);
    const selectedCategoryValue = contactUsReasonsWithTranslations.find(
      reason => reason.label === selectedCategory
    )?.value;
    const statusCode = await createInquiry({
      tabAction,
      ...data,
      category: selectedCategoryValue
    });

    if (statusCode >= 500 && statusCode < 600) {
      onError(true);
      return;
    }

    if (statusCode === 200) {
      onError(false);
      onSuccess(true);
      reset();
      setShowDropdowns(false);
      setTimeout(() => {
        setShowDropdowns(true);
      }, 1);
      sessionStorage.clear();
    }
  };

  return (
    <form
      id="form"
      className="contactUsPage__section__form"
      onSubmit={handleSubmit(submitForm)}
    >
      <CatTextPassage>
        <p
          className={cx(
            styles['contactUsPage__section__form__first_section__description'],
            'cat-u-padding-top-md cat-u-padding-bottom-md my-0',
            MD
              ? 'cat-u-theme-typography-body'
              : 'cat-u-theme-typography-body-sm'
          )}
          data-testid="your-question-text"
        >
          {t('1294_YOUR_QUESTION_TEXT')}.
        </p>
      </CatTextPassage>
      <div className="contactUsPage__section__form__first_section cat-u-padding-bottom-lg">
        <div className="cat-u-padding-bottom-md">
          <Conditional test={showDropdowns}>
            <CatDropdown
              label={t('1294_CATEGORY')}
              placeholder={t('SELECT_AN_OPTION')}
              value={selectedCategory}
              onBlSelect={handleSelectCategory}
              {...commonProperties('category', {
                errorMessage: t('SELECT_CATEGORY')
              })}
            >
              <CatList spacing="padded">
                {contactUsReasonsWithTranslations.map(reason => (
                  <CatListItem value={reason.label} key={reason.key}>
                    {reason.label}
                  </CatListItem>
                ))}
              </CatList>
            </CatDropdown>
          </Conditional>
        </div>
        <div className="cat-u-padding-bottom-md">
          <CatTextareaField
            fieldNote={t('1294_YOUR_QUESTION_DISCLOSURE')}
            rows="3"
            label={t('1294_YOUR_QUESTION_INPUT_LABEL')}
            placeholder=""
            size="md"
            {...commonInputProperties('comments', { required: false })}
          />
        </div>
        <div className="cat-u-padding-bottom-md">
          <CatInputField
            label={t('CAT_ORDER_SERIAL_NO')}
            {...commonInputProperties('serialNumber', { required: false })}
            data-cs-mask
          />
        </div>
      </div>
      <div className="contactUsPage__section__form__second_section d-flex flex-column">
        <div className="cat-u-padding-bottom-md">
          <CatHeading level="h5" variant={MD ? 'title' : 'headline'}>
            {t('YOUR_CONTACT_INFO')}
          </CatHeading>
        </div>
        <div className={cx(['d-flex flex-column'])}>
          <div
            className={cx('cat-u-padding-bottom-md', {
              [styles['contactUsPage__section__form__input__order-second']]:
                isPageInJapanese
            })}
          >
            <CatInputField
              label={t('CAT_EMAIL_FIRSTNAME')}
              {...commonInputProperties('firstName', {
                errorMessage: t('ENTER_FIRST_NAME')
              })}
            />
          </div>
          <div
            className={cx('cat-u-padding-bottom-md', {
              [styles['contactUsPage__section__form__input__order-first']]:
                isPageInJapanese
            })}
          >
            <CatInputField
              label={t('CAT_EMAIL_LASTNAME')}
              {...commonInputProperties('lastName', {
                errorMessage: t('ENTER_LAST_NAME')
              })}
            />
          </div>
        </div>
        <div className="cat-u-padding-bottom-md">
          <CatInputField
            label={t('CAT_MA_EMAIL')}
            type="email"
            {...commonInputProperties('email', {
              errorMessage: t('ENTER_YOUR_EMAIL')
            })}
          />
        </div>
        <div className="cat-u-padding-bottom-md">
          <Conditional test={showDropdowns}>
            <CatDropdown
              label={t('COUNTRY_REGION')}
              placeholder={t('SELECT_AN_OPTION')}
              onBlSelect={handleSelectCountry}
              value={selectedCountry}
              {...commonProperties('country', {
                errorMessage: t('SELECT_YOUR_COUNTRY')
              })}
            >
              <CatList spacing="padded">
                {countriesList.map(country => (
                  <CatListItem key={country.key} value={country.value}>
                    {country.label}
                  </CatListItem>
                ))}
              </CatList>
            </CatDropdown>
          </Conditional>
        </div>
        <div
          className={cx('cat-u-padding-bottom-md', {
            [styles['contactUsPage__section__form__input__order-third']]:
              isPageInJapanese
          })}
        >
          <CatInputField
            label={t('MA_CITY')}
            {...commonInputProperties('city', {
              errorMessage: t('ENTER_CITY')
            })}
          />
        </div>
        <div
          className={cx('cat-u-padding-bottom-md', {
            [styles['contactUsPage__section__form__input__order-second']]:
              isPageInJapanese
          })}
        >
          {statesListOfSelectedCountry.length > 0 ? (
            <Conditional test={showDropdowns}>
              <CatDropdown
                label={t('CAT_EMAIL_STATE')}
                placeholder={t('SELECT_AN_OPTION')}
                onBlSelect={handleSelectState}
                value={selectedState}
                {...commonProperties('state', {
                  errorMessage: t('SELECT_YOUR_STATE_LC')
                })}
              >
                <CatList spacing="padded">
                  {statesListOfSelectedCountry.map(state => (
                    <CatListItem value={state.value} key={state.key}>
                      {state.label}
                    </CatListItem>
                  ))}
                </CatList>
              </CatDropdown>
            </Conditional>
          ) : (
            <CatInputField
              label={t('CAT_EMAIL_STATE')}
              {...commonInputProperties('state', {
                errorMessage: t('SELECT_YOUR_STATE_LC')
              })}
            />
          )}
        </div>
        <div
          className={cx('cat-u-padding-bottom-md', {
            [styles['contactUsPage__section__form__input__order-first']]:
              isPageInJapanese
          })}
        >
          <CatInputField
            label={t('ZIPCODE_POSTALCODE')}
            {...commonInputProperties('zipCode', {
              errorMessage: t('ENTER_ZIP_CODE')
            })}
          />
        </div>
      </div>
      <CatButton
        fullWidth={MD ? false : true}
        form="form"
        type="submit"
        variant="primary"
        disabled={isSubmitting}
      >
        {t('SUBMIT')}
      </CatButton>
      <CatTextPassage>
        <p
          className={cx(
            styles['contactUsPage__section__form__first_section__message'],
            'cat-u-theme-typography-body-sm cat-u-padding-top-md cat-u-margin-none'
          )}
        >
          {t('YOUR_CONTACT_INFORMATION_MESSAGE')}
        </p>
      </CatTextPassage>
    </form>
  );
};

ContactUsForm.propTypes = {
  tabAction: PropTypes.string,
  onError: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired
};

export default ContactUsForm;
